<template>
  <uiv-popover
    ref="uivPopup"
    v-bind="$attrs"
    v-on="$listeners"
    @show="onShow"
    @hide="onHide">
    <slot/>
    <template slot="popover"><slot name="popover"/></template>
  </uiv-popover>
</template>

<script>
import { Popover } from 'uiv'
import { placePopupBesideTrigger } from './a11y_popup_placement'

export default {
  name: 'A11yPopover',
  components: { 'uiv-popover': Popover },
  inheritAttrs: false,
  props: {
    // CSS selector for a DOM parent to relocate the shown popup into,
    // instead of the trigger's own parent. For a trigger whose parent
    // enforces ARIA owned-element rules (e.g. a `role="listbox"` option)
    // that a relocated popup would violate — see BrowseStandardSelector.vue.
    hostSelector: { type: String, default: null }
  },
  methods: {
    onShow() {
      this.a11yDescribe()
      // WCAG 1.4.13 (Dismissible): content shown on hover/focus must be
      // dismissible with Escape without moving pointer or focus. uiv's Popup
      // has no keyboard handling, so add it here at the shared wrapper.
      document.addEventListener('keydown', this.onDocumentKeydown, true)
      // WCAG 1.3.2 (Meaningful Sequence): relocate the popup beside its
      // trigger instead of leaving it at the end of <body> (uiv's default).
      const uiv = this.$refs.uivPopup
      const popup = uiv && uiv.$refs.popup
      // Marker class scopes the `.popover-title` line-height rule below to
      // this wrapper's own popup, not every `.popover-title` in the app.
      if (popup) popup.classList.add('a11y-popover-popup')
      // uiv can cancel a pending hide and re-emit 'show' (fast hover-away-
      // then-back) without running resetPosition(), leaving our placement's
      // viewport-fixed values in place — only (re)place when nothing is
      // already tracking it, or we'd double-apply the scroll offset.
      if (!this._releasePopupPlacement) {
        const hostEl = this.hostSelector ? document.querySelector(this.hostSelector) : undefined
        this._releasePopupPlacement = placePopupBesideTrigger(popup, uiv && uiv.triggerEl, hostEl)
      }
    },
    onHide() {
      this.a11yUndescribe()
      document.removeEventListener('keydown', this.onDocumentKeydown, true)
      if (this._releasePopupPlacement) {
        this._releasePopupPlacement()
        this._releasePopupPlacement = null
      }
    },
    onDocumentKeydown(e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return
      const uiv = this.$refs.uivPopup
      if (uiv && typeof uiv.isShown === 'function' && uiv.isShown()) {
        // Don't steal focus — hide only. Re-showing requires a fresh
        // hover/focus, which satisfies the "dismiss without moving focus" rule.
        uiv.hide()
        // A trigger nested inside another Escape-handling ancestor — e.g. uiv's
        // own Dropdown (BrowseStandardSelector.vue's standards listbox) — would
        // otherwise also see this keydown and run its own Escape handling
        // (closing the dropdown and refocusing its toggle), moving focus off
        // the trigger and breaking the "without moving focus" guarantee above.
        e.stopPropagation()
      }
    },
    a11yDescribe() {
      const uiv = this.$refs.uivPopup
      const popup = uiv && uiv.$refs.popup
      const trigger = uiv && uiv.triggerEl
      if (!popup || !trigger) return
      if (!popup.id) {
        popup.id = `a11y-popup-${this._uid}`
      }
      const ids = (trigger.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter(Boolean)
      if (!ids.includes(popup.id)) {
        ids.push(popup.id)
      }
      trigger.setAttribute('aria-describedby', ids.join(' '))
    },
    a11yUndescribe() {
      const uiv = this.$refs.uivPopup
      const popup = uiv && uiv.$refs.popup
      const trigger = uiv && uiv.triggerEl
      if (!popup || !trigger) return
      const ids = (trigger.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter(Boolean)
        .filter((id) => id !== popup.id)
      if (ids.length) {
        trigger.setAttribute('aria-describedby', ids.join(' '))
      } else {
        trigger.removeAttribute('aria-describedby')
      }
    },
  },
  beforeDestroy() {
    this.a11yUndescribe()
    document.removeEventListener('keydown', this.onDocumentKeydown, true)
    if (this._releasePopupPlacement) {
      this._releasePopupPlacement()
      this._releasePopupPlacement = null
    }
  },
}
</script>

<style>
/*
 * Relocating the popup beside its trigger (a11y_popup_placement.js) drops it
 * into whatever CSS the host component declares, so a host's own unscoped
 * heading rule (e.g. WikiTextBlock.vue's `.wiki-text-container h1,h2,h3`)
 * can out-specify Bootstrap's `.popover-title` line-height. `!important`
 * defends against that; `1.1` mirrors Bootstrap's `@headings-line-height`.
 * Plain CSS (not LESS) so the exact text can be read directly in the spec.
 * Scoped to `.a11y-popover-popup` (set in onShow()), not bare
 * `.popover-title`, so this doesn't leak onto other popovers on the page.
 */
.a11y-popover-popup .popover-title {
  line-height: 1.1 !important;
}
</style>
